import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

// Define interfaces (unchanged)
interface SettingFormData {
  logo: File | null;
  logoPath: string;
  email: string;
  name: string;
  password: string;
  sidebarColor: string;
  headerColor: string;
  settingId?: string;
  menuActiveColor: string;
  fontColor: string;
  logoName: string;
  mailMailer: string;
  mailHost: string;
  mailPort: string;
}

interface SettingResponse {
  status: number;
  data: {
    id?: string;
    setting_id?: string;
    email?: string;
    name?: string;
    password?: string;
    sidebar_color?: string;
    header_color?: string;
    logo_path?: string;
    menu_active_color?: string;
    font_color?: string;
    logoname?: string | null;
    mail_mailer?: string;
    mail_host?: string;
    mail_port?: string;
  }[];
  message?: string;
}

interface ThemeResponse {
  status: number;
  message: string;
}

interface EmailResponse {
  status: number;
  message: string;
}

interface LogoResponse {
  status: number;
  message: string | { company_logo?: string[] };
}

interface SettingsContextType {
  settings: SettingFormData;
  updateSettings: (newSettings: SettingFormData) => void;
  fetchSettings: () => Promise<void>;
  updateLogo: (settingId: string, logo: File | null, logoName: string) => Promise<boolean>;
  updateThemeColors: (settingId: string, colors: {
    sidebarColor: string;
    headerColor: string;
    fontColor: string;
    menuActiveColor: string;
  }) => Promise<boolean>;
  updateEmailSettings: (settingId: string, emailSettings: {
    email: string;
    name: string; // Add name to the emailSettings type
    password: string;
    mailMailer: string;
    mailHost: string;
    mailPort: string;
  }) => Promise<boolean>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Define defaultSettings to provide fallback values
const defaultSettings: SettingFormData = {
  logo: null,
  logoPath: "./images/logo/main-logo.webp", // Provide a default favicon path
  email: "",
  name: "",
  password: "",
  sidebarColor: "#000000",
  headerColor: "#000000",
  menuActiveColor: "#FFFFFF",
  fontColor: "#000000",
  settingId: "",
  logoName: "SalesBox", // Provide a default app name
  mailMailer: "",
  mailHost: "",
  mailPort: "",
};

const loadSettingsFromStorage = (): SettingFormData => {
  const storedSettings = localStorage.getItem("appSettings");
  if (storedSettings) {
    return JSON.parse(storedSettings);
  }
  return defaultSettings; // Use defaultSettings instead of inline defaults
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SettingFormData>(loadSettingsFromStorage());
  const location = useLocation();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");

  useEffect(() => {
    const logoTitle = settings.logoName?.trim();

    if (logoTitle && logoTitle !== "null" && logoTitle !== "undefined") {
      document.title = `${logoTitle} | Powered by KloudData`;
    } else {
      document.title = "Powered by KloudData";
    }

    // Update favicon (this part remains the same)
    const logoPath = settings.logoPath?.trim();
    const isValidLogoPath = logoPath && logoPath !== "null" && logoPath !== "undefined";

    const faviconUrl = `${isValidLogoPath ? logoPath : defaultSettings.logoPath}?v=${Date.now()}`;
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      favicon.setAttribute("href", faviconUrl);
    } else {
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    }
  }, [settings.logoName, settings.logoPath]);




  // Rest of the code remains unchanged
  useEffect(() => {
    localStorage.setItem("appSettings", JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem("custom_logoPath", settings.logoPath);
    localStorage.setItem("custom_logoName", settings.logoName);
  }, [settings.logoPath, settings.logoName]);

  const convertToHexColor = (color: string): string => {
    try {
      if (/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(color)) {
        return color.slice(0, 7);
      }
      const num = parseInt(color, 10);
      if (!isNaN(num)) {
        const hex = num.toString(16).padStart(6, "0");
        return `#${hex.slice(-6)}`;
      }
      const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10).toString(16).padStart(2, "0");
        const g = parseInt(rgbMatch[2], 10).toString(16).padStart(2, "0");
        const b = parseInt(rgbMatch[3], 10).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`;
      }
      const colorMap: { [key: string]: string } = {
        black: "#000000",
        white: "#FFFFFF",
        red: "#FF0000",
        green: "#00FF00",
        blue: "#0000FF",
      };
      const lowerColor = color.toLowerCase();
      if (colorMap[lowerColor]) {
        return colorMap[lowerColor];
      }
      console.warn(`Unsupported color format: ${color}`);
      return "#000000";
    } catch (error) {
      console.error("Invalid color format:", color, error);
      return "#000000";
    }
  };

  const fetchSettings = useCallback(async () => {
    if (!token) {
      navigate("/signin");
      return;
    }

    try {
      const response = await axios.get<SettingResponse>(
        `${process.env.REACT_APP_API_URL}/api/setting/edit`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
        const settingsData = response.data.data[0];
        const settingId = settingsData.setting_id || settingsData.id;

        if (!settingId) {
          throw new Error("Setting ID not found in response");
        }

        const {
          email,
          password,
          sidebar_color,
          header_color,
          logo_path,
          menu_active_color,
          font_color,
          logoname,
          mail_mailer,
          mail_host,
          mail_port,
        } = settingsData;

        const convertedSidebarColor = sidebar_color
          ? convertToHexColor(sidebar_color)
          : "#000000";
        const convertedHeaderColor = header_color
          ? convertToHexColor(header_color)
          : "#000000";
        const convertedMenuActiveColor = menu_active_color
          ? convertToHexColor(menu_active_color)
          : "#FFFFFF";
        const convertedFontColor = font_color
          ? convertToHexColor(font_color)
          : "#000000";
        const newSettings: SettingFormData = {
          logo: null,
          logoPath: logo_path &&
            logo_path.trim() !== "" &&
            logo_path.trim().toLowerCase() !== "null" &&
            logo_path.trim().toLowerCase() !== "undefined"
            ? logo_path
            : defaultSettings.logoPath,


          logoName: logoname && logoname.trim().toLowerCase() !== "null" && logoname.trim() !== ""
            ? logoname
            : "",
          email: email || "",
          name: settingsData.name || "",
          password: password || "",
          sidebarColor: convertedSidebarColor,
          headerColor: convertedHeaderColor,
          menuActiveColor: convertedMenuActiveColor,
          fontColor: convertedFontColor,
          settingId,
          mailMailer: mail_mailer || "",
          mailHost: mail_host || "",
          mailPort: mail_port || "",
        };

        setSettings(newSettings);
      } else {
        throw new Error(response.data.message || "Settings not found");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch settings data";
      if (error.response?.status === 401) {
        sessionStorage.removeItem("access_token"); // Clear invalid token
        navigate("/signin");
      } else if (!location.pathname.includes("/signin")) {
        if (errorMessage.includes("Setting Not Found")) {
          toast.dismiss();
          // toast.error("Settings not found on the server. Please create a default setting or contact support.");
        } else {
          toast.dismiss();
          // toast.error(`Error fetching settings: ${errorMessage}`);
        }
      }
    }
  }, [token, location.pathname, navigate]);

  const updateLogo = useCallback(
    async (settingId: string, logo: File | null, logoName: string): Promise<boolean> => {
      if (!token) {
        navigate("/signin");
        return false;
      }
      try {
        const formData = new FormData();
        formData.append("setting_id", settingId);
        if (logo) {
          formData.append("company_logo", logo);
        }

        if (logoName && logoName.trim() !== "") {
          formData.append("logoname", logoName);
        } else {
          formData.append("logoname", ""); // Explicitly clear it
        }


        const response = await axios.post<LogoResponse>(
          `${process.env.REACT_APP_API_URL}/api/settings/logo`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.status === 200) {
          await fetchSettings();
          const setDynamicFavicon = async () => {
            try {
              const token = sessionStorage.getItem("access_token");
              if (!token) {
                return;
              }

              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/setting/edit`, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              });

              const result = await response.json();

              if (result.status === 200 && result.data.length > 0) {
                const faviconUrl = result.data[0].logo_path;

                // Find or create the favicon link element
                let link = document.querySelector("link[rel*='icon']");
                if (!link) {
                  link = document.createElement("link");
                  document.head.appendChild(link);
                }

                const faviconLink = link as HTMLLinkElement;
                faviconLink.type = "image/png";
                faviconLink.rel = "icon";
                faviconLink.href = faviconUrl;
              }
            } catch (error) {
              console.error("Error setting favicon:", error);
            }
          };
          setDynamicFavicon();
          return true;
        } else {
          throw new Error(
            typeof response.data.message === "string"
              ? response.data.message
              : response.data.message?.company_logo?.[0] || "Failed to update logo"
          );
        }
      } catch (error: any) {
        console.error("Error updating logo:", error);
        const errorMessage =
          error.response?.data?.status === 422 && error.response?.data?.message?.company_logo?.[0]
            ? error.response.data.message.company_logo[0]
            : error.response?.data?.message || error.message || "Failed to update logo";
        if (error.response?.status === 401) {
          sessionStorage.removeItem("access_token");
          navigate("/signin");
        }
        throw new Error(errorMessage);
      }
    },
    [token, navigate, fetchSettings]
  );

  const updateThemeColors = useCallback(
    async (settingId: string, colors: {
      sidebarColor: string;
      headerColor: string;
      fontColor: string;
      menuActiveColor: string;
    }): Promise<boolean> => {
      if (!token) {
        navigate("/signin");
        return false;
      }

      try {
        const response = await axios.post<ThemeResponse>(
          `${process.env.REACT_APP_API_URL}/api/settings/theme`,
          {
            setting_id: settingId,
            sidebar_color: colors.sidebarColor,
            header_color: colors.headerColor,
            font_color: colors.fontColor,
            menu_active_color: colors.menuActiveColor,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === 200) {
          await fetchSettings();
          return true;
        } else {
          throw new Error(response.data.message || "Failed to update theme colors");
        }
      } catch (error: any) {
        console.error("Error updating theme colors:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to update theme colors";
        if (error.response?.status === 401) {
          sessionStorage.removeItem("access_token");
          navigate("/signin");
        } else {
          toast.dismiss();
          toast.error(`Error updating theme colors: ${errorMessage}`);
        }
        return false;
      }
    },
    [token, navigate, fetchSettings]
  );

  const updateEmailSettings = useCallback(
    async (settingId: string, emailSettings: {
      email: string;
      password: string;
      mailMailer: string;
      mailHost: string;
      mailPort: string;
      name: string;
    }): Promise<boolean> => {
      if (!token) {
        navigate("/signin");
        return false;
      }

      try {
        const formData = new FormData();
        formData.append("setting_id", settingId);
        formData.append("email", emailSettings.email);
        formData.append("password", emailSettings.password);
        formData.append("mail_mailer", emailSettings.mailMailer);
        formData.append("mail_host", emailSettings.mailHost);
        formData.append("mail_port", emailSettings.mailPort);
        formData.append("name", emailSettings.name);

        const response = await axios.post<EmailResponse>(
          `${process.env.REACT_APP_API_URL}/api/settings/email`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.status === 200) {
          await fetchSettings();
          return true;
        } else {
          throw new Error(response.data.message || "Failed to update email settings");
        }
      } catch (error: any) {
        console.error("Error updating email settings:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to update email settings";
        if (error.response?.status === 401) {
          sessionStorage.removeItem("access_token");
          navigate("/signin");
        } else {
          toast.dismiss();
          toast.error(`Error updating email settings: ${errorMessage}`);
        }
        return false;
      }
    },
    [token, navigate, fetchSettings]
  );

  useEffect(() => {
    if (token && !location.pathname.includes("/signin")) {
      fetchSettings();
    }
  }, [fetchSettings, token, location.pathname]);

  const updateSettings = (newSettings: SettingFormData) => {
    setSettings(newSettings);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, fetchSettings, updateLogo, updateThemeColors, updateEmailSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};